import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { Field, Formik } from "formik";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";
import { QRCodeCanvas } from "qrcode.react";
import moment from "moment";
import { Move, ZoomIn, ZoomOut } from "lucide-react";
import CloseIcon from "../../assets/icons/close-icon";
import SearchIcon from "../../assets/icons/search-icon";
import InputField from "../forms/input-field";
import CheckBox from "../forms/check-box-field";
import ToggleSwitch from "../forms/toggle-switch";
import SelectField from "../forms/select-field";
import AddButton, { DraftButton } from "../buttons/button";
import errorToast from "./error-toast";
import successToast from "./success-toast";
import ConfirmModal from "./confirm-modal";
import { isAdmin } from "../../redux/config";
import {
  savePrintLabelSettingsAction,
  getPrintLabelSettingsAction,
} from "../../redux/actions/purchase-action";
import {
  displayHtmlContent,
  escapeHtml,
  printHtml,
} from "./helper-function";
import {
  PRINT_STATE,
  buildTsplJob,
  clientOsName,
  getLastPrinterError,
  getSavedThermalPrinter,
  launchQzTray,
  onPrinterStatusChange,
  openQzDownload,
  qzDownloadUrl,
  listPrinters,
  pickThermalPrinter,
  printTsplLabels,
  saveThermalPrinter,
} from "./tsc-label-printer";

const PAGE_WIDTH_MM = 210;
const PAGE_HEIGHT_MM = 297;
// Default thermal label size — fits 58mm paper (48mm printable width)
const THERMAL_LABEL_WIDTH_MM = 48;
const THERMAL_LABEL_HEIGHT_MM = 25;

function thermalLabelSize(values) {
  return {
    width:
      Math.max(0.1, Number(values.thermalLabelWidth) || 0) ||
      THERMAL_LABEL_WIDTH_MM,
    height:
      Math.max(0.1, Number(values.thermalLabelHeight) || 0) ||
      THERMAL_LABEL_HEIGHT_MM,
  };
}
const SETTINGS_STORAGE_KEY = "printBarcodeLabelSettings";

// width/height are chosen so that, at the default margins (5.2mm L/R,
// 10mm T/B), gridMetrics' fit-based column/row count actually reproduces
// the declared "columns"/"count" here — e.g. 5 columns of 52.5mm (262.5mm)
// can never fit an A4 page's 210mm width no matter the margin, so that
// preset would always render fewer columns than advertised.
const SHEET_PRESETS = [
  { value: "84", count: 84, columns: 4, width: 48, height: 13 },
  { value: "65", count: 65, columns: 5, width: 38.1, height: 21.2 },
  { value: "40", count: 40, columns: 5, width: 37, height: 32 },
  { value: "30", count: 30, columns: 3, width: 64, height: 26.5 },
  { value: "24", count: 24, columns: 3, width: 58, height: 33 },
  { value: "21", count: 21, columns: 3, width: 58, height: 37 },
  { value: "12", count: 12, columns: 3, width: 58, height: 62 },
];
// The preset auto-applied whenever "Codes" is switched to QR.
const QR_DEFAULT_PRESET_VALUE = "84";
// The preset auto-applied whenever "Codes" is switched back to Barcode
// (also the fallback if "84" was selected while a barcode is now active).
const BARCODE_DEFAULT_PRESET_VALUE = "65";
// "84 Labels" is QR-only — its 13mm height is too tight for a barcode's
// fuller content stack, so it's hidden from the dropdown in barcode mode.
const QR_ONLY_PRESET_VALUES = ["84"];
// "65" and "40 Labels" are disabled for a barcode that also carries the
// batch number.
const GS1_BARCODE_DISABLED_PRESET_VALUES = ["65", "40"];
// Fallback preset for a barcode when the batch number is embedded.
const GS1_BARCODE_DEFAULT_PRESET_VALUE = "30";
const SHEET_PRESET_OPTIONS = SHEET_PRESETS.map((preset) => ({
  value: preset.value,
  label: `${preset.value} Labels`,
}));

const FIELD_OPTIONS = [
  { key: "mrp", label: "MRP" },
  { key: "expiryDate", label: "Expiry Date" },
  { key: "storeName", label: "Store Name" },
  { key: "saleRate", label: "Sale Rate" },
  { key: "batchNo", label: "Batch No" },
  { key: "location", label: "Location" },
  { key: "invoiceDate", label: "Invoice Date" },
  { key: "distributor", label: "Distributor" },
];

const DEFAULT_SETTINGS = {
  show: {
    mrp: false,
    expiryDate: true,
    storeName: true,
    saleRate: true,
    batchNo: true,
    location: false,
    invoiceDate: true,
    distributor: true,
  },
  printerType: "a4",
  codeType: "barcode",
  // What the barcode / QR encodes: "itemBarcode" (plain item_barcode) or
  // "itemBarcodeBatch" (GS1 element string with item_barcode + batch no.).
  codeContent: "itemBarcode",
  labelsOnSheet: "65",
  labelsPerRow: 5,
  labelWidth: 39,
  labelHeight: 20.95,
  marginTopBottom: 10,
  marginLeftRight: 5.2,
  blankPositions: "",
  thermalGap: 0.2,
  thermalLabelWidth: THERMAL_LABEL_WIDTH_MM,
  thermalLabelHeight: THERMAL_LABEL_HEIGHT_MM,
};

function mergeWithDefaultSettings(parsed) {
  const merged = {
    ...DEFAULT_SETTINGS,
    ...parsed,
    show: { ...DEFAULT_SETTINGS.show, ...parsed?.show },
  };
  // A saved default may hold a preset that's no longer allowed for its code
  // settings (e.g. saved before a preset was restricted) — swap it for the
  // matching fallback rather than opening on an unselectable option.
  if (merged.codeType !== "qr") {
    const isGs1Barcode = merged.codeContent === "itemBarcodeBatch";
    const isUnavailable =
      QR_ONLY_PRESET_VALUES.includes(merged.labelsOnSheet) ||
      (isGs1Barcode &&
        GS1_BARCODE_DISABLED_PRESET_VALUES.includes(merged.labelsOnSheet));
    if (isUnavailable) {
      const fallback = SHEET_PRESETS.find(
        (p) =>
          p.value ===
          (isGs1Barcode
            ? GS1_BARCODE_DEFAULT_PRESET_VALUE
            : BARCODE_DEFAULT_PRESET_VALUE)
      );
      if (fallback) {
        merged.labelsOnSheet = fallback.value;
        merged.labelsPerRow = fallback.columns;
        merged.labelWidth = fallback.width;
        merged.labelHeight = fallback.height;
      }
    }
  }
  return merged;
}

function readSavedSettings() {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw);
    return mergeWithDefaultSettings(parsed);
  } catch {
    return DEFAULT_SETTINGS;
  }
}

function parseBlankPositions(text) {
  const positions = new Set();
  String(text || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const rangeMatch = part.match(/^(\d+)\s*-\s*(\d+)$/);
      if (rangeMatch) {
        const start = parseInt(rangeMatch[1], 10);
        const end = parseInt(rangeMatch[2], 10);
        for (let i = start; i <= end; i++) positions.add(i);
      } else if (/^\d+$/.test(part)) {
        positions.add(parseInt(part, 10));
      }
    });
  return positions;
}

function gridMetrics(values) {
  const marginTopBottom = Number(values.marginTopBottom) || 0;
  const marginLeftRight = Number(values.marginLeftRight) || 0;
  const labelWidth = Math.max(0.1, Number(values.labelWidth) || 0.1);
  const labelHeight = Math.max(0.1, Number(values.labelHeight) || 0.1);

  // Strict priority order: margins and the label count per page are the
  // fixed constraints and are never compromised. Label size is used
  // exactly as typed for the cell's own box, and the GAP between cells
  // absorbs whatever the numbers demand — including going negative
  // (labels overlapping) rather than ever changing the count or margin.
  const preset =
    SHEET_PRESETS.find((p) => p.value === values.labelsOnSheet) ||
    SHEET_PRESETS[0];
  const columns = Math.max(1, Number(values.labelsPerRow) || preset.columns || 1);
  const totalCount = Math.max(columns, Number(preset.count) || columns);
  const rows = Math.max(1, Math.ceil(totalCount / columns));
  const count = columns * rows;

  const usableWidth = PAGE_WIDTH_MM - 2 * marginLeftRight;
  const usableHeight = PAGE_HEIGHT_MM - 2 * marginTopBottom;
  const colGap =
    columns > 1 ? (usableWidth - columns * labelWidth) / (columns - 1) : 0;
  const rowGap =
    rows > 1 ? (usableHeight - rows * labelHeight) / (rows - 1) : 0;

  return { columns, rows, count, colGap, rowGap, usableWidth, usableHeight };
}

function buildLayoutCells(items, labelCounts, blankPositions) {
  const queue = [];
  items.forEach((item, index) => {
    const count = Math.max(0, parseInt(labelCounts[index], 10) || 0);
    for (let i = 0; i < count; i++) queue.push(item);
  });

  const cells = [];
  let cursor = 0;
  let position = 1;
  while (cursor < queue.length) {
    if (blankPositions.has(position)) {
      cells.push({ blank: true, position });
    } else {
      cells.push({ blank: false, item: queue[cursor], position });
      cursor += 1;
    }
    position += 1;
  }
  return cells;
}

function paginate(cells, perSheet) {
  if (!perSheet || perSheet <= 0) return cells.length ? [cells] : [[]];
  const sheets = [];
  for (let i = 0; i < cells.length; i += perSheet) {
    sheets.push(cells.slice(i, i + perSheet));
  }
  return sheets.length ? sheets : [[]];
}

const RIGHT_STRIP_CELL_WIDTH_PX = 0;
const PX_TO_MM = 25.4 / 96;
// Shared by the preview's <QRCodeCanvas size={...}> and the print QR
// image's CSS size, so the QR code is the same physical size in both.
const QR_CODE_SIZE_PX = 60;

// Shared by the live preview and the print output, which both use the same
// SVG data URL so the rendered barcode never drifts apart between the two.
// The barcode is vector, so the printer rasterises each bar at its own
// resolution; an integer module width keeps every bar a whole unit wide
// (a fractional width anti-aliases bar edges and scanners misread them).
// margin is the quiet zone — 10 modules, as CODE128 requires.
const BARCODE_RENDER_OPTIONS = {
  format: "CODE128",
  width: 2,
  height: 40,
  displayValue: false,
  margin: 20,
};

const GS1_CODE_CONTENT = "itemBarcodeBatch";
// JsBarcode's CODE128 FNC1 character — separates variable-length GS1 fields.
const GS1_BARCODE_SEPARATOR = String.fromCharCode(207);
// ASCII GS — the standard FNC1 stand-in inside a QR code's GS1 payload.
const GS1_QR_SEPARATOR = String.fromCharCode(29);

function isGs1(codeContent) {
  return codeContent === GS1_CODE_CONTENT;
}

// GS1 element string for item_barcode + batch number. A numeric GTIN
// (8/12/13/14 digits) goes in AI (01) zero-padded to 14 digits; any other
// item barcode isn't a GTIN, so it goes in AI (240) "additional product
// identification". The batch goes in AI (10), max 20 chars, last.
function gs1Payload(item, separator) {
  const barcode = String(item?.item_barcode ?? "").trim();
  const batch = String(item?.item_batch_number ?? "").trim().slice(0, 20);
  const isGtin = /^(\d{8}|\d{12,14})$/.test(barcode);
  const elements = [
    isGtin
      ? { ai: "01", value: barcode.padStart(14, "0"), fixedLength: true }
      : { ai: "240", value: barcode.slice(0, 30), fixedLength: false },
    batch && { ai: "10", value: batch, fixedLength: false },
  ].filter(Boolean);
  return elements
    .map((element, index) => {
      const isLast = index === elements.length - 1;
      const needsSeparator = !element.fixedLength && !isLast;
      return `${element.ai}${element.value}${needsSeparator ? separator : ""}`;
    })
    .join("");
}

function codeValueFor(item, codeType, codeContent) {
  if (!isGs1(codeContent) || !item?.item_barcode) {
    return item?.item_barcode || "0";
  }
  return gs1Payload(
    item,
    codeType === "qr" ? GS1_QR_SEPARATOR : GS1_BARCODE_SEPARATOR
  );
}

function barcodeRenderOptions(codeContent) {
  // ean128 prefixes the leading FNC1 that marks the barcode as GS1-128.
  return isGs1(codeContent)
    ? { ...BARCODE_RENDER_OPTIONS, ean128: true }
    : BARCODE_RENDER_OPTIONS;
}

function applySheetPreset(setFieldValue, presetValue) {
  const preset = SHEET_PRESETS.find((p) => p.value === presetValue);
  if (!preset) return;
  setFieldValue("labelsOnSheet", preset.value);
  setFieldValue("labelsPerRow", preset.columns);
  setFieldValue("labelWidth", preset.width);
  setFieldValue("labelHeight", preset.height);
}


function rateSlotText(item, show) {
  if (show.mrp) return item?.item_batch_unit_price ?? 0;
  if (show.saleRate) return item?.item_batch_purchase_rate ?? 0;
  return null;
}

function rightStripLines(item, show) {
  const rate = rateSlotText(item, show);
  return [
    rate != null && { key: "rate", text: `₹${rate}` },
    show.invoiceDate &&
      item?.created_date && {
        key: "date",
        text: moment(item.created_date).format("DD/MM/YYYY"),
      },
    show.storeName &&
      item?.company && { key: "store", text: item.company },
  ].filter(Boolean);
}

function captionLines(item, show) {
  return [
    show.distributor && item?.cmr_name,
    show.location && item?.bin_location_no,
  ].filter(Boolean);
}

const verticalTextStyle = {
  writingMode: "vertical-rl",
  transform: "rotate(180deg)",
  whiteSpace: "nowrap",
};

function CaptionBlock({ lines }) {
  if (lines.length === 0) return null;
  return (
    <div className="text-[6px] text-secondary leading-tight ">
      {lines.map((line, index) => (
        <div key={index} className="truncate">
          {line}
        </div>
      ))}
    </div>
  );
}

function LabelFace({ item, values, style }) {
  const isQr = values.codeType === "qr";
  const codeValue = codeValueFor(item, values.codeType, values.codeContent);
  const barcodeUrl = useMemo(
    () => (isQr ? "" : barcodeDataUrl(codeValue, values.codeContent)),
    [isQr, codeValue, values.codeContent]
  );

  const caption = captionLines(item, values.show);

  if (isQr) {
    const rate = rateSlotText(item, values.show);
    return (
      <div
        style={style}
        className="rounded-lg border border-D0D5DD shadow-sm bg-white flex flex-col justify-between overflow-hidden px-[1.5mm] py-[1mm]"
      >        
        <div className="flex items-center gap-[1mm] text-[6.5px] fw_500 text-secondary min-w-0 flex-1 min-h-0">
          <QRCodeCanvas
            value={codeValue}
            size={QR_CODE_SIZE_PX}
            className="shrink-0"
            style={{ height: "100%", width: "auto", maxWidth: "45%" }}
          />
          <div className="flex flex-col justify-center min-w-0 leading-tight">
            <div className="text-[6.5px] fw_700 leading-tight">
              {item?.item_name}
            </div>
            <div className="flex flex-wrap items-center min-w-0 leading-tight gap-x-1">
              {values.show.batchNo && item?.item_batch_number && (
                <span className="whitespace-nowrap">
                  {item.item_batch_number}
                </span>
              )}

              {values.show.expiryDate && item?.item_exp_date && (
                <span className="whitespace-nowrap">
                  {moment(item.item_exp_date).format("DD/MM/YY")}
                </span>
              )}

              {rate != null && (
                <span className="fw_700 text-000000 whitespace-nowrap">
                  ₹{rate}
                </span>
              )}
            </div>
            <CaptionBlock lines={caption} />
          </div>
        </div>
        {values.show.storeName && item?.company && (
          <div className="text-[6px] text-secondary truncate leading-tight">
            {item.company}
          </div>
        )}
      </div>
    );
  }

  const rightLines = rightStripLines(item, values.show);

  return (
    <div
      style={style}
      className="rounded-lg border border-D0D5DD shadow-sm bg-white flex overflow-hidden px-[0.5mm] py-[0.8mm]"
    >
      <div className="flex items-center justify-center shrink-0 gap-0">
        <span
          style={verticalTextStyle}
          className="text-[6.5px] fw_600 text-secondary"
        >
          {item?.item_barcode}
        </span>
      </div>

      <div className="flex flex-col flex-1 min-w-0 justify-between px-[0.5mm] py-[1mm]">
        <div className="text-[7.5px] fw_700 leading-tight truncate">
          {item?.item_name}
        </div>
        <div className="flex justify-between items-baseline gap-[1mm] text-[6.5px] fw_500 text-secondary min-w-0">
          {values.show.batchNo && (
            <span className="truncate">{item?.item_batch_number}</span>
          )}
          {values.show.expiryDate && item?.item_exp_date && (
            <span className="shrink-0">
              {moment(item.item_exp_date).format("DD/MM/YY")}
            </span>
          )}
        </div>
        <div className="flex items-center justify-center flex-1 min-h-0 overflow-hidden mb-[0.25mm]">
          {barcodeUrl && (
            <img
              src={barcodeUrl}
              alt=""
              style={{ height: "100%", width: "auto", maxWidth: "100%" }}
            />
          )}
        </div>
        <CaptionBlock lines={caption} />
      </div>

      {rightLines.length > 0 && (
        <div className="flex shrink-0 items-center gap-0">
          {rightLines.map((line) => (
            <div
              key={line.key}
              className="flex items-center justify-center"
              style={{
                minWidth: `${RIGHT_STRIP_CELL_WIDTH_PX}px`,
                height: "100%",
              }}
            >
              <span
                style={{
                  ...verticalTextStyle,
                  whiteSpace: "normal",
                  maxHeight: "100%",
                  lineHeight: 1.15,
                  textAlign: "center",
                }}
                className="text-[6.5px] fw_600 text-secondary"
              >
                {line.text}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SheetPage({ cells, values, metrics, pageIndex, totalPages }) {
  return (
    <div
      className="relative bg-white shadow-xl shrink-0"
      style={{ width: `${PAGE_WIDTH_MM}mm`, height: `${PAGE_HEIGHT_MM}mm` }}
    >
      <div
        className="absolute"
        style={{
          top: `${values.marginTopBottom}mm`,
          left: `${values.marginLeftRight}mm`,
          width: `${metrics.usableWidth}mm`,
          height: `${metrics.usableHeight}mm`,
        }}
      >
        {cells.map((cell, index) => {
          const col = index % metrics.columns;
          const row = Math.floor(index / metrics.columns);
          const cellStyle = {
            position: "absolute",
            left: `${col * (Number(values.labelWidth) + metrics.colGap)}mm`,
            top: `${row * (Number(values.labelHeight) + metrics.rowGap)}mm`,
            width: `${values.labelWidth}mm`,
            height: `${values.labelHeight}mm`,
          };
          return cell.blank ? (
            <div key={index} style={cellStyle} />
          ) : (
            <LabelFace
              key={index}
              item={cell.item}
              values={values}
              style={cellStyle}
            />
          );
        })}
      </div>
      <div className="absolute bottom-[1mm] right-[2mm] text-[7px] text-secondary">
        Sheet {pageIndex + 1} / {totalPages}
      </div>
    </div>
  );
}

function ThermalPreview({ cells, values }) {
  const printableCells = cells.filter((cell) => !cell.blank);
  const gap = Number(values.thermalGap) || 0;
  const { width, height } = thermalLabelSize(values);
  return (
    <div
      className="flex flex-col items-center shrink-0"
      style={{ gap: `${gap}mm` }}
    >
      {printableCells.map((cell, index) => (
        <LabelFace
          key={index}
          item={cell.item}
          values={values}
          style={{ width: `${width}mm`, height: `${height}mm` }}
        />
      ))}
    </div>
  );
}

const barcodeUrlCache = new Map();
function barcodeDataUrl(value, codeContent) {
  // A plain value can match a GS1 one character-for-character, but the GS1
  // render adds a leading FNC1, so the cache is keyed on both.
  const cacheKey = `${isGs1(codeContent)}|${value}`;
  if (barcodeUrlCache.has(cacheKey)) return barcodeUrlCache.get(cacheKey);
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  let dataUrl = "";
  try {
    JsBarcode(svg, value || "0", barcodeRenderOptions(codeContent));
    // crispEdges snaps each bar to whole device dots instead of blending
    // its edges grey; "none" lets the bars stretch to fill the label slot
    // (a uniform horizontal stretch keeps CODE128 bar ratios intact).
    svg.setAttribute("shape-rendering", "crispEdges");
    svg.setAttribute("preserveAspectRatio", "none");
    const markup = new XMLSerializer().serializeToString(svg);
    dataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`;
  } catch {}
  barcodeUrlCache.set(cacheKey, dataUrl);
  return dataUrl;
}

const qrUrlCache = new Map();
async function qrDataUrl(value) {
  if (qrUrlCache.has(value)) return qrUrlCache.get(value);
  const dataUrl = await QRCode.toDataURL(value || "0", {
    margin: 0,
    width: 110,
  });
  qrUrlCache.set(value, dataUrl);
  return dataUrl;
}

async function codeDataUrlFor(item, codeType, codeContent) {
  const value = codeValueFor(item, codeType, codeContent);
  return codeType === "qr"
    ? qrDataUrl(value)
    : barcodeDataUrl(value, codeContent);
}

function captionHtml(item, show) {
  const lines = captionLines(item, show);
  if (lines.length === 0) return "";
  const linesHtml = lines
    .map((line) => `<div class="caption-line">${escapeHtml(line)}</div>`)
    .join("");
  return `<div class="caption">${linesHtml}</div>`;
}

async function labelFaceHtml(item, values, widthMm, heightMm) {
  const sizeStyle = `width:${widthMm}mm;${
    heightMm ? `height:${heightMm}mm;` : ""
  }`;
  const caption = captionHtml(item, values.show);

  if (values.codeType === "qr") {
    const codeUrl = await codeDataUrlFor(
      item,
      values.codeType,
      values.codeContent
    );
    const rate = rateSlotText(item, values.show);
    const metaCells = [
      values.show.batchNo &&
        item?.item_batch_number &&
        `<span>${escapeHtml(item.item_batch_number)}</span>`,
      values.show.expiryDate &&
        item?.item_exp_date &&
        `<span>${moment(item.item_exp_date).format("DD/MM/YY")}</span>`,
      rate != null && `<span class="rate">₹${escapeHtml(String(rate))}</span>`,
    ]
      .filter(Boolean)
      .join("");
    const storeNameHtml =
      values.show.storeName && item?.company
        ? `<div class="caption-line">${escapeHtml(item.company)}</div>`
        : "";
    return `<div class="label label-qr" style="${sizeStyle}">
      <div class="qr-row">
        <img class="qr-code" src="${codeUrl}" />
        <div class="qr-text">
          <div class="name">${escapeHtml(item?.item_name)}</div>
          <div class="qr-meta">${metaCells}</div>
          ${caption}
        </div>
      </div>
      ${storeNameHtml}
    </div>`;
  }

  const codeUrl = await codeDataUrlFor(
    item,
    values.codeType,
    values.codeContent
  );
  const rightLines = rightStripLines(item, values.show)
    .map((line) => `<div class="cell"><span>${escapeHtml(line.text)}</span></div>`)
    .join("");
  return `<div class="label label-barcode" style="${sizeStyle}">
    <div class="strip left"><span>${escapeHtml(item?.item_barcode)}</span></div>
    <div class="body">
      <div class="name">${escapeHtml(item?.item_name)}</div>
      <div class="row">
        ${
          values.show.batchNo
            ? `<span>${escapeHtml(item?.item_batch_number)}</span>`
            : ""
        }
        ${
          values.show.expiryDate && item?.item_exp_date
            ? `<span>${moment(item.item_exp_date).format("DD/MM/YY")}</span>`
            : ""
        }
      </div>
      <div class="code"><img src="${codeUrl}" /></div>
      ${caption}
    </div>
    ${rightLines ? `<div class="strip right">${rightLines}</div>` : ""}
  </div>`;
}

const LABEL_STYLES = `
  .label { border: 1px solid #000; border-radius: 8px; overflow: hidden; background: #fff; }
  .label .name { font-size: 7.5px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .label .caption-line { font-size: 6px; line-height: 1.25; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

  .label-barcode { display: flex; padding: 0.8mm 0.5mm; }
  .label-barcode .strip.left { display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .label-barcode .strip.left span { writing-mode: vertical-rl; transform: rotate(180deg); white-space: nowrap; font-size: 6.5px; font-weight: 600; }
  .label-barcode .strip.right { display: flex; flex-direction: row; align-items: center; flex-shrink: 0; }
  .label-barcode .strip.right .cell { display: flex; align-items: center; justify-content: center; min-width: ${(
    RIGHT_STRIP_CELL_WIDTH_PX * PX_TO_MM
  ).toFixed(2)}mm; height: 100%; }
  .label-barcode .strip.right .cell span { writing-mode: vertical-rl; transform: rotate(180deg); white-space: normal; line-height: 1.15; max-height: 100%; text-align: center; font-size: 6.5px; font-weight: 600; }
  .label-barcode .body { display: flex; flex-direction: column; justify-content: space-between; flex: 1; min-width: 0; padding: 1mm 0.5mm; overflow: hidden; }
  .label-barcode .row { display: flex; justify-content: space-between; gap: 1mm; font-size: 6.5px; font-weight: 500; }
  .label-barcode .code { flex: 1; display: flex; align-items: center; justify-content: center; overflow: hidden; min-height: 0; }
  .label-barcode .code img { height: 100%; width: auto; max-width: 100%; }

  .label-qr { display: flex; flex-direction: column; justify-content: space-between; padding: 1mm 1.5mm; }
  .label-qr .qr-row { display: flex; align-items: center; gap: 1mm; font-size: 6.5px; font-weight: 500; flex: 1; min-height: 0; }
  .label-qr .qr-code { height: 100%; width: auto; max-width: 45%; flex-shrink: 0; }
  .label-qr .qr-text { display: flex; flex-direction: column; justify-content: center; min-width: 0; line-height: 1.15; }
  .label-qr .name { font-size: 6.5px; font-weight: 700; line-height: 1.15; white-space: normal; }
  .label-qr .qr-meta { display: flex; flex-wrap: wrap; align-items: center; column-gap: 1mm; min-width: 0; line-height: 1.15; }
  .label-qr .qr-meta span { white-space: nowrap; }
  .label-qr .rate { font-weight: 700; }
`;

async function buildPrintHtml(sheets, values, metrics) {
  if (values.printerType === "thermal") {
    const printableCells = (sheets[0] || []).filter((cell) => !cell.blank);
    const { width, height } = thermalLabelSize(values);
    const labelsHtml = await Promise.all(
      printableCells.map((cell) =>
        labelFaceHtml(cell.item, values, width, height)
      )
    );
    // Each label is its own page sized exactly to the label (plus the gap to
    // the next one), so the printer driver feeds one label per page instead
    // of falling back to A4/Letter — "size: <w> auto" is not valid CSS.
    const pageHeight = height + (Number(values.thermalGap) || 0);
    return `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>
      @page { size: ${width}mm ${pageHeight}mm; margin: 0; }
      * { box-sizing: border-box; }
      html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; }
      .page { width: ${width}mm; height: ${pageHeight}mm; overflow: hidden; page-break-after: always; break-after: page; }
      .page:last-child { page-break-after: auto; break-after: auto; }
      ${LABEL_STYLES}
    </style></head><body>${labelsHtml
      .map((label) => `<div class="page">${label}</div>`)
      .join("")}</body></html>`;
  }

  const pagesHtml = await Promise.all(
    sheets.map(async (cells) => {
      const cellsHtml = await Promise.all(
        cells.map(async (cell, index) => {
          const col = index % metrics.columns;
          const row = Math.floor(index / metrics.columns);
          const left = col * (Number(values.labelWidth) + metrics.colGap);
          const top = row * (Number(values.labelHeight) + metrics.rowGap);
          const posStyle = `position:absolute;left:${left}mm;top:${top}mm;`;
          const inner = cell.blank
            ? `<div style="width:${values.labelWidth}mm;height:${values.labelHeight}mm;"></div>`
            : await labelFaceHtml(
                cell.item,
                values,
                values.labelWidth,
                values.labelHeight
              );
          return `<div style="${posStyle}">${inner}</div>`;
        })
      );
      return `<div class="sheet" style="width:${PAGE_WIDTH_MM}mm;height:${PAGE_HEIGHT_MM}mm;">
        <div style="position:relative;margin:${values.marginTopBottom}mm 0 0 ${values.marginLeftRight}mm;width:${metrics.usableWidth}mm;height:${metrics.usableHeight}mm;">${cellsHtml.join(
        ""
      )}</div>
      </div>`;
    })
  );

  return `<!DOCTYPE html><html><head><meta charset="utf-8" /><style>
    @page { size: A4; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, sans-serif; }
    .sheet { page-break-after: always; overflow: hidden; }
    .sheet:last-child { page-break-after: auto; }
    ${LABEL_STYLES}
  </style></head><body>${pagesHtml.join("")}</body></html>`;
}

const PrintBarcodeModal = ({ isOpen, onClose, items = [] }) => {
  const dispatch = useDispatch();
  const [printing, setPrinting] = useState(false);
  const [zoom, setZoom] = useState(2);
  const [itemSearch, setItemSearch] = useState("");
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const [remoteSettings, setRemoteSettings] = useState(null);
  // Printers reported by the JSPrintManager client app; null until checked,
  // [] when the app isn't running (thermal then falls back to browser print).
  const [thermalPrinters, setThermalPrinters] = useState(null);
  const [thermalPrinter, setThermalPrinter] = useState(getSavedThermalPrinter);
  const [printState, setPrintState] = useState(null);
  const [checkingPrinters, setCheckingPrinters] = useState(false);
  const [launchingQz, setLaunchingQz] = useState(false);
  const previewRef = useRef(null);

  const refreshThermalPrinters = async ({ force = false } = {}) => {
    setCheckingPrinters(true);
    try {
      const { state, printers } = await listPrinters({ force });
      setPrintState(state);
      setThermalPrinters(printers);
      setThermalPrinter((current) =>
        pickThermalPrinter(printers, current || getSavedThermalPrinter())
      );
    } finally {
      setCheckingPrinters(false);
    }
  };

  const startQzTray = async () => {
    setLaunchingQz(true);
    try {
      await launchQzTray();
      await refreshThermalPrinters();
    } finally {
      setLaunchingQz(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    refreshThermalPrinters();
    // Reload printers as soon as QZ Tray connects (e.g. a late Allow).
    return onPrinterStatusChange((state) => {
      setPrintState(state);
      if (state === PRINT_STATE.OPEN) refreshThermalPrinters();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const storeId = localStorage.getItem("warehouse_id");
    if (!storeId) return;
    dispatch(
      getPrintLabelSettingsAction(
        { store_id: storeId },
        (data) => {
          if (data?.data) setRemoteSettings(data.data);
        },
        () => {}
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialValues = useMemo(() => {
    const saved = remoteSettings
      ? mergeWithDefaultSettings(remoteSettings)
      : readSavedSettings();
    return {
      ...saved,
      labelCounts: items.map((item) =>
        Math.max(0, Math.round(parseFloat(item?.item_qty) || 0))
      ),
    };
  }, [items, remoteSettings]);

  const resetZoom = () => {
    setZoom(2);
    if (previewRef.current) previewRef.current.scrollTo(0, 0);
  };

  const innerOnSubmit = async (values) => {
    const blankPositions = parseBlankPositions(values.blankPositions);
    const cells = buildLayoutCells(items, values.labelCounts, blankPositions);
    if (cells.every((cell) => cell.blank)) {
      errorToast("Add at least one label to print");
      return;
    }
    const metrics = gridMetrics(values);
    const sheets =
      values.printerType === "thermal"
        ? [cells]
        : paginate(cells, metrics.count);

    setPrinting(true);
    try {
      if (values.printerType === "thermal" && thermalPrinter) {
        const { width, height } = thermalLabelSize(values);
        const labels = cells
          .filter((cell) => !cell.blank)
          .map((cell) => ({
            item: cell.item,
            codeValue: codeValueFor(
              cell.item,
              values.codeType,
              values.codeContent
            ),
          }));
        await printTsplLabels(
          thermalPrinter,
          buildTsplJob(labels, values, {
            widthMm: width,
            heightMm: height,
            gapMm: Number(values.thermalGap) || 0,
            renderOptions: barcodeRenderOptions(values.codeContent),
            rateText: (item) => rateSlotText(item, values.show),
          })
        );
        saveThermalPrinter(thermalPrinter);
        successToast(`Sent ${labels.length} label(s) to ${thermalPrinter}`);
        onClose();
        return;
      }
      const html = await buildPrintHtml(sheets, values, metrics);
      if (isAdmin) {
        displayHtmlContent(html);
      } else {
        const { width, height } = thermalLabelSize(values);
        printHtml(
          html,
          values.printerType === "thermal"
            ? {
                widthMm: width,
                heightMm: height + (Number(values.thermalGap) || 0),
              }
            : undefined
        );
      }
      onClose();
    } catch (error) {
      console.error("Print barcode labels failed", error);
      errorToast("Could not generate the print preview. Please try again.");
    } finally {
      setPrinting(false);
    }
  };

  return (
    <Transition show={!!isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-50 overflow-y-auto"
        onClose={onClose}
      >
        <div className="flex items-center justify-center min-h-screen px-2 text-center">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-100"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-60" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="mx-auto w-full max-w-[98vw] h-[94vh] my-4 text-left align-middle transition-all transform bg-white shadow-xl rounded-xl flex flex-col overflow-hidden">
              <Formik
                enableReinitialize={true}
                initialValues={initialValues}
                onSubmit={innerOnSubmit}
              >
                {({ values, setFieldValue, handleSubmit, resetForm }) => {
                  const blankPositions = parseBlankPositions(
                    values.blankPositions
                  );
                  const cells = buildLayoutCells(
                    items,
                    values.labelCounts,
                    blankPositions
                  );
                  const metrics = gridMetrics(values);
                  const sheets =
                    values.printerType === "thermal"
                      ? [cells]
                      : paginate(cells, metrics.count);
                  const isThermal = values.printerType === "thermal";
                  const itemSearchQuery = itemSearch.trim().toLowerCase();
                  const filteredItems = itemSearchQuery
                    ? items
                        .map((item, index) => ({ item, index }))
                        .filter(({ item }) =>
                          [item?.item_code, item?.item_name].some((field) =>
                            String(field || "")
                              .toLowerCase()
                              .includes(itemSearchQuery)
                          )
                        )
                    : [];

                  return (
                    <>
                      <div className="flex justify-between items-center px-6 pt-4 shrink-0">
                        <h2 className="text-secondary fw_600 text-lg">
                          Print Barcodes / QR's ({items?.[0]?.gort_invoice_number})
                        </h2>
                        <button
                          type="button"
                          className="close-button bg-transparent border-none p-0 hover:bg-787878"
                          onClick={onClose}
                        >
                          <CloseIcon />
                        </button>
                      </div>

                      {items.length === 0 ? (
                        <div className="px-6 py-10 text-center text-secondary text-sm">
                          No items found to print
                        </div>
                      ) : (
                        <>
                          <div className="px-6 pt-3 shrink-0 flex items-center justify-between gap-3">
                            <div className="relative w-64">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2">
                                <SearchIcon />
                              </span>
                              <input
                                type="text"
                                value={itemSearch}
                                onChange={(e) => setItemSearch(e.target.value)}
                                placeholder="Search by item code or name"
                                className="w-full border border-D0D5DD rounded-md pl-9 pr-3 py-1.5 text-sm text-secondary focus:outline-none focus:border-primary"
                              />
                            </div>
                            <button
                              type="button"
                              className="text-xs text-red-600 border border-red-600 rounded-md px-3 py-1.5 fw_500 whitespace-nowrap"
                              onClick={() => setConfirmClearOpen(true)}
                            >
                              Set all Labels Qty as 0
                            </button>
                          </div>

                          <div className="px-6 pt-2 shrink-0">
                            <div className="max-h-[22vh] overflow-y-auto outline outline-[var(--list-table-border-color)] outline-1 border-radius">
                              <table className="w-full border-collapse mt-0">
                                <thead className="sticky top-0 bg-[var(--list-table-heading-bg)] z-[10]">
                                  <tr>
                                    {[
                                      "Item Code",
                                      "Item",
                                      "Batch",
                                      "Expiry",
                                      "Received Qty",
                                      "No. of Labels",
                                    ].map((label) => (
                                      <th
                                        key={label}
                                        className="border-b border-[var(--list-table-border-color)] px-[8px] h-[50px] text-left whitespace-nowrap text-[var(--list-table-heading-fg-color)] text-[length:--list-table-heading-font-size] font-[var(--list-table-heading-font-weight)]"
                                      >
                                        {label.toUpperCase()}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {filteredItems.length === 0 ? (
                                    <tr style={{ height: "8rem" }}>
                                      <td
                                        colSpan={6}
                                        className="text-center text-primary"
                                      >
                                        {itemSearchQuery
                                          ? "No items match this item code or name"
                                          : "Search by item code or name to edit its label count"}
                                      </td>
                                    </tr>
                                  ) : (
                                    filteredItems.map(({ item, index }) => (
                                      <tr
                                        key={`${item?.item_code}-${item?.item_batch_number}-${index}`}
                                        className="hover:bg-[var(--list-table-row-hover-bg)] bg-[var(--list-table-row-bg)] h-[50px]"
                                      >
                                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                          {item?.item_code}
                                        </td>
                                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                          {item?.item_name}
                                        </td>
                                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                          {item?.item_batch_number}
                                        </td>
                                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                          {item?.item_exp_date
                                            ? moment(item.item_exp_date).format(
                                                "DD MMM YYYY"
                                              )
                                            : "-"}
                                        </td>
                                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] text-left whitespace-nowrap text-[length:--list-table-row-font-size] font-[var(--list-table-row-font-weight)] text-[var(--list-table-row-fg-color)]">
                                          {item?.item_qty}
                                        </td>
                                        <td className="border-b border-[var(--list-table-border-color)] px-[8px] py-1 text-left w-32">
                                          <InputField
                                            isContainer={false}
                                            label=""
                                            type="number"
                                            min={0}
                                            name={`labelCounts[${index}]`}
                                            isRequired={false}
                                            onBlur={(e) => {
                                              const num = Math.max(
                                                0,
                                                Math.floor(
                                                  Number(e.target.value) || 0
                                                )
                                              );
                                              setFieldValue(
                                                `labelCounts[${index}]`,
                                                num
                                              );
                                            }}
                                          />
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          <ConfirmModal
                            isOpen={confirmClearOpen}
                            title="Set all label quantities to 0?"
                            message="This will set the number of labels to print for every item in this list to 0."
                            confirmText="Yes"
                            cancelText="No"
                            onConfirm={() => {
                              setFieldValue(
                                "labelCounts",
                                items.map(() => 0)
                              );
                              setConfirmClearOpen(false);
                            }}
                            onCancel={() => setConfirmClearOpen(false)}
                          />

                          <div className="flex flex-1 min-h-0 px-6 py-4 gap-4">
                            <div
                              className="flex-1 min-w-0 rounded-lg overflow-auto relative"
                              style={{ backgroundColor: "var(--slide-panel-special-bg)" }}
                              ref={previewRef}
                            >
                              <div
                                className="p-8 flex flex-col items-center gap-6 w-fit"
                                style={{
                                  transform: `scale(${zoom})`,
                                  transformOrigin: "top left",
                                }}
                              >
                                {isThermal ? (
                                  <ThermalPreview cells={cells} values={values} />
                                ): (
                                  sheets.map((sheetCells, index) => (
                                    <SheetPage
                                      key={index}
                                      cells={sheetCells}
                                      values={values}
                                      metrics={metrics}
                                      pageIndex={index}
                                      totalPages={sheets.length}
                                    />
                                  ))
                                )}
                              </div>
                            </div>

                            <div className="w-[280px] shrink-0 overflow-y-auto">
                              <div className="flex justify-between items-baseline mb-2">
                                <h3 className="text-secondary fw_600 text-sm">
                                  Settings
                                </h3>
                                <div className="text-xs flex gap-2">
                                  <button
                                    type="button"
                                    className="text-primary underline"
                                    onClick={() => {
                                      localStorage.setItem(
                                        SETTINGS_STORAGE_KEY,
                                        JSON.stringify({
                                          show: values.show,
                                          printerType: values.printerType,
                                          codeType: values.codeType,
                                          codeContent: values.codeContent,
                                          labelsOnSheet: values.labelsOnSheet,
                                          labelsPerRow: values.labelsPerRow,
                                          labelWidth: values.labelWidth,
                                          labelHeight: values.labelHeight,
                                          marginTopBottom: values.marginTopBottom,
                                          marginLeftRight: values.marginLeftRight,
                                          blankPositions: values.blankPositions,
                                          thermalGap: values.thermalGap,
                                          thermalLabelWidth:
                                            values.thermalLabelWidth,
                                          thermalLabelHeight:
                                            values.thermalLabelHeight,
                                        })
                                      );

                                      const storeId =
                                        localStorage.getItem("warehouse_id");
                                      if (!storeId) return;
                                      const { labelCounts, ...settingsToSave } =
                                        values;
                                      dispatch(
                                        savePrintLabelSettingsAction(
                                          {
                                            store_id: storeId,
                                            settings: settingsToSave,
                                          },
                                          () => {
                                            setRemoteSettings(settingsToSave);
                                            successToast(
                                              "Default settings saved"
                                            );
                                          },
                                          (error) => {
                                            errorToast(
                                              error?.data?.message ||
                                                "Could not save default settings"
                                            );
                                          }
                                        )
                                      );
                                    }}
                                  >
                                    Save as default
                                  </button>
                                  <span className="text-secondary">|</span>
                                  <button
                                    type="button"
                                    className="text-primary underline"
                                    onClick={() => resetForm()}
                                  >
                                    Reset
                                  </button>
                                </div>
                              </div>

                              <CheckBox
                                conClass="grid grid-cols-2 gap-y-2"
                                items={FIELD_OPTIONS.map((opt) => ({
                                  label: opt.label,
                                  name: `show.${opt.key}`,
                                  isRequired: false,
                                }))}
                                onChange={(name, checked) => {
                                  // MRP and Sale Rate share one slot on the label — mutually exclusive.
                                  if (name === "show.mrp" && checked) {
                                    setFieldValue("show.saleRate", false);
                                  }
                                  if (name === "show.saleRate" && checked) {
                                    setFieldValue("show.mrp", false);
                                  }
                                }}
                              />

                              <div className="mt-4">
                                <p className="text-[10px] uppercase tracking-wide text-secondary fw_500 mb-1">
                                  On scanning, the code gives
                                </p>
                                <ToggleSwitch
                                  checked={isGs1(values.codeContent)}
                                  onClick={() => {
                                    const nextGs1 = !isGs1(values.codeContent);
                                    setFieldValue(
                                      "codeContent",
                                      nextGs1 ? GS1_CODE_CONTENT : "itemBarcode"
                                    );
                                    if (
                                      nextGs1 &&
                                      values.codeType === "barcode" &&
                                      GS1_BARCODE_DISABLED_PRESET_VALUES.includes(
                                        values.labelsOnSheet
                                      )
                                    ) {
                                      applySheetPreset(
                                        setFieldValue,
                                        GS1_BARCODE_DEFAULT_PRESET_VALUE
                                      );
                                    }
                                  }}
                                  primaryText="Item code only"
                                  secondaryText="Item code + Batch no."
                                  primaryTextClass="text-xs text-secondary"
                                  secondaryTextClass="text-xs text-secondary"
                                />
                              </div>

                              {isThermal && (
                                <>
                                  <div className="flex flex-col items-start gap-1 w-full mt-4">
                                    <div className="flex justify-between w-full">
                                      <span className="text-[10px] uppercase tracking-wide text-secondary fw_500">
                                        Thermal Printer
                                      </span>
                                      <button
                                        type="button"
                                        className="text-[10px] text-primary underline disabled:opacity-50"
                                        disabled={checkingPrinters}
                                        onClick={() =>
                                          refreshThermalPrinters({ force: true })
                                        }
                                      >
                                        {checkingPrinters
                                          ? "Checking…"
                                          : "Refresh"}
                                      </button>
                                    </div>
                                    {thermalPrinters?.length ? (
                                      <select
                                        className="w-full min-h-[36px] rounded-[8px] border border-D0D5DD px-2 text-xs bg-white"
                                        value={thermalPrinter}
                                        onChange={(event) =>
                                          setThermalPrinter(event.target.value)
                                        }
                                      >
                                        <option value="">
                                          Print dialog
                                        </option>
                                        {thermalPrinters.map((name) => (
                                          <option key={name} value={name}>
                                            {name}
                                          </option>
                                        ))}
                                      </select>
                                    ) : launchingQz ? (
                                      <p className="w-full rounded-[8px] border border-D0D5DD bg-white p-2 text-[10px] text-secondary leading-snug">
                                        Starting QZ Tray… If the browser asks to
                                        open QZ Tray, click Open (tick "Always
                                        allow" to skip this next time).
                                      </p>
                                    ) : thermalPrinters === null ? (
                                      <p className="text-[10px] text-secondary leading-snug">
                                        Looking for printers…
                                      </p>
                                    ) : printState === PRINT_STATE.INSECURE_PAGE ? (
                                      <div className="w-full rounded-[8px] border border-D0D5DD bg-white p-2 text-[10px] text-secondary leading-snug">
                                        <p>
                                          This page is opened over http from{" "}
                                          <span className="fw_600">
                                            {window.location.host}
                                          </span>
                                          , so the browser blocks it from
                                          talking to QZ Tray on this computer.
                                        </p>
                                        <p className="mt-2">
                                          Open the app over https, or — if the
                                          app is served from this same computer
                                          — open it as{" "}
                                          <span className="fw_600">
                                            {`${window.location.protocol}//localhost${
                                              window.location.port
                                                ? `:${window.location.port}`
                                                : ""
                                            }${window.location.pathname}`}
                                          </span>
                                          , then click Refresh. The desktop app
                                          prints directly without this.
                                        </p>
                                      </div>
                                    ) : printState === PRINT_STATE.CONNECTING ? (
                                      <p className="w-full rounded-[8px] border border-D0D5DD bg-white p-2 text-[10px] text-secondary leading-snug">
                                        Waiting for permission to reach QZ
                                        Tray. Click the icon at the left of the
                                        address bar and turn on{" "}
                                        <span className="fw_600">
                                          Apps on device
                                        </span>
                                        , then reload. If a QZ Tray window asks
                                        to allow this site, click Allow — it
                                        may be behind this window, check the
                                        taskbar. Printers load automatically.
                                      </p>
                                    ) : printState === PRINT_STATE.OPEN ? (
                                      <p className="w-full rounded-[8px] border border-D0D5DD bg-white p-2 text-[10px] text-secondary leading-snug">
                                        No printers found. Check the TSC driver
                                        is installed, then click Refresh (and
                                        Allow if QZ Tray asks).
                                        {getLastPrinterError() && (
                                          <span className="mt-1 block break-words text-[9px] opacity-80">
                                            Details: {getLastPrinterError()}
                                          </span>
                                        )}
                                      </p>
                                    ) : (
                                      <div className="w-full rounded-[8px] border border-D0D5DD bg-white p-2 text-[10px] text-secondary leading-snug">
                                        <p>
                                          QZ Tray is not running. Labels open in
                                          the print dialog until it is.
                                        </p>
                                        <button
                                          type="button"
                                          onClick={startQzTray}
                                          className="mt-2 rounded-md bg-primary px-3 py-1 text-xs text-white"
                                        >
                                          Start QZ Tray
                                        </button>
                                        <p className="mt-3 fw_600">
                                          Not installed yet? One-time setup:
                                        </p>
                                        <ol className="mt-1 list-decimal pl-4 space-y-1">
                                          <li>
                                            <a
                                              href={qzDownloadUrl()}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              onClick={(event) => {
                                                event.preventDefault();
                                                openQzDownload();
                                              }}
                                              className="inline-block rounded-md bg-primary px-3 py-1 text-xs text-white"
                                            >
                                              Download QZ Tray
                                              {clientOsName()
                                                ? ` for ${clientOsName()}`
                                                : ""}
                                            </a>{" "}
                                            and run the installer.
                                          </li>
                                          <li>
                                            Click Start QZ Tray above (or open
                                            it from the Start menu).
                                          </li>
                                          <li>
                                            Click the icon at the left of the
                                            address bar and turn on{" "}
                                            <span className="fw_600">
                                              Apps on device
                                            </span>
                                            , then reload this page.
                                          </li>
                                          <li>
                                            Click Refresh above, and Allow this
                                            site when QZ Tray asks.
                                          </li>
                                        </ol>
                                        {getLastPrinterError() && (
                                          <p className="mt-2 break-words text-[9px] opacity-80">
                                            Details: {getLastPrinterError()}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 mt-4">
                                    <InputField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      type="number"
                                      step="any"
                                      extraAllowedChars="."
                                      min={0}
                                      name="thermalLabelWidth"
                                      label="Label Width (mm)"
                                      isRequired={false}
                                      labelWidth="auto"
                                    />
                                    <InputField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      type="number"
                                      step="any"
                                      extraAllowedChars="."
                                      min={0}
                                      name="thermalLabelHeight"
                                      label="Label Height (mm)"
                                      isRequired={false}
                                      labelWidth="auto"
                                    />
                                  </div>
                                  <InputField
                                    parentClass="flex flex-col items-start gap-1 w-full mt-4"
                                    labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                    type="number"
                                    step="any"
                                    extraAllowedChars="."
                                    min={0}
                                    name="thermalGap"
                                    label="Gap Between Labels (mm)"
                                    isRequired={false}
                                    labelWidth="auto"
                                  />
                                </>
                              )}

                              {!isThermal && (
                                <>
                                  <div className="grid grid-cols-2 gap-3 mt-4">
                                    <SelectField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      label="Labels on A4 Sheet"
                                      labelWidth="auto"
                                      options={SHEET_PRESET_OPTIONS.filter(
                                        (option) =>
                                          values.codeType === "qr" ||
                                          !QR_ONLY_PRESET_VALUES.includes(
                                            option.value
                                          )
                                      ).map((option) =>
                                        values.codeType === "barcode" &&
                                        isGs1(values.codeContent) &&
                                        GS1_BARCODE_DISABLED_PRESET_VALUES.includes(
                                          option.value
                                        )
                                          ? { ...option, isDisabled: true }
                                          : option
                                      )}
                                      name="labelsOnSheet"
                                      menuPosition="fixed"
                                      handleSelected={(option) => {
                                        const preset = SHEET_PRESETS.find(
                                          (p) => p.value === option?.value
                                        );
                                        if (preset) {
                                          setFieldValue(
                                            "labelsPerRow",
                                            preset.columns
                                          );
                                          setFieldValue(
                                            "labelWidth",
                                            preset.width
                                          );
                                          setFieldValue(
                                            "labelHeight",
                                            preset.height
                                          );
                                        }
                                      }}
                                    />
                                    <InputField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      type="number"
                                      min={1}
                                      isDisabled={true}
                                      name="labelsPerRow"
                                      label="Labels per Row"
                                      isRequired={false}
                                      labelWidth="auto"
                                    />
                                  </div>

                                  <div className="grid grid-cols-2 gap-3 mt-3">
                                    <InputField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      type="number"
                                      step="any"
                                      extraAllowedChars="."
                                      min={5}
                                      name="labelWidth"
                                      label="Label Width (mm)"
                                      isRequired={false}
                                      labelWidth="auto"
                                      
                                    />
                                    <InputField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      type="number"
                                      step="any"
                                      extraAllowedChars="."
                                      min={5}
                                      name="labelHeight"
                                      label="Label Height (mm)"
                                      isRequired={false}
                                      labelWidth="auto"
                                    />
                                  </div>

                                  <div className="grid grid-cols-2 gap-3 mt-3">
                                    <InputField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      type="number"
                                      step="any"
                                      extraAllowedChars="."
                                      min={0}
                                      name="marginTopBottom"
                                      label="Top-Bottom Gap (mm)"
                                      isRequired={false}
                                      labelWidth="auto"
                                    />
                                    <InputField
                                      parentClass="flex flex-col items-start gap-1 w-full"
                                      labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                      type="number"
                                      step="any"
                                      extraAllowedChars="."
                                      min={0}
                                      name="marginLeftRight"
                                      label="Left-Right Gap (mm)"
                                      isRequired={false}
                                      labelWidth="auto"
                                    />
                                  </div>

                                  <InputField
                                    parentClass="flex flex-col items-start gap-1 w-full"
                                    labelClass="text-[10px] uppercase tracking-wide text-secondary fw_500"
                                    type="text"
                                    name="blankPositions"
                                    label="Labels to be left blank"
                                    placeholder="e.g. 1,2,5-7"
                                    isRequired={false}
                                    labelWidth="auto"
                                    containerClass="mt-3"
                                    allowSpecial={true}
                                  />
                                </>
                              )}
                            </div>
                          </div>

                          <div className="border-t border-D0D5DD px-6 py-3 flex items-center justify-between shrink-0">
                            <div className="flex flex-wrap gap-6">
                              <div>
                                <p className="text-secondary fw_500 text-xs mb-1">
                                  Printer:
                                </p>
                                <div className="flex gap-2">
                                  {[
                                    { value: "a4", label: "A4 Printer" },
                                    { value: "thermal", label: "Thermal" },
                                  ].map((option) => (
                                    <label
                                      key={option.value}
                                      className={`px-3 py-1 rounded-md border cursor-pointer text-xs whitespace-nowrap ${
                                        values.printerType === option.value
                                          ? "bg-primary text-white border-primary"
                                          : "border-D0D5DD text-secondary"
                                      }`}
                                    >
                                      <Field
                                        type="radio"
                                        name="printerType"
                                        value={option.value}
                                        className="hidden"
                                      />
                                      {option.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-secondary fw_500 text-xs mb-1">
                                  Codes:
                                </p>
                                <div className="flex gap-2">
                                  {[
                                    { value: "barcode", label: "Barcode" },
                                    { value: "qr", label: "QR Code" },
                                  ].map((option) => (
                                    <label
                                      key={option.value}
                                      className={`px-3 py-1 rounded-md border cursor-pointer text-xs whitespace-nowrap ${
                                        values.codeType === option.value
                                          ? "bg-primary text-white border-primary"
                                          : "border-D0D5DD text-secondary"
                                      }`}
                                      onClick={() => {
                                        const isGs1Content = isGs1(
                                          values.codeContent
                                        );
                                        if (
                                          option.value === "qr" &&
                                          values.codeType !== "qr"
                                        ) {
                                          applySheetPreset(
                                            setFieldValue,
                                            QR_DEFAULT_PRESET_VALUE
                                          );
                                        } else if (
                                          option.value === "barcode" &&
                                          values.codeType !== "barcode" &&
                                          (QR_ONLY_PRESET_VALUES.includes(
                                            values.labelsOnSheet
                                          ) ||
                                            (isGs1Content &&
                                              GS1_BARCODE_DISABLED_PRESET_VALUES.includes(
                                                values.labelsOnSheet
                                              )))
                                        ) {
                                          applySheetPreset(
                                            setFieldValue,
                                            isGs1Content
                                              ? GS1_BARCODE_DEFAULT_PRESET_VALUE
                                              : BARCODE_DEFAULT_PRESET_VALUE
                                          );
                                        }
                                      }}
                                    >
                                      <Field
                                        type="radio"
                                        name="codeType"
                                        value={option.value}
                                        className="hidden"
                                      />
                                      {option.label}
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                title="Zoom out"
                                onClick={() =>
                                  setZoom((z) => Math.max(0.25, +(z - 0.1).toFixed(2)))
                                }
                                className="p-2 rounded-md border border-D0D5DD hover:bg-F2F4F5"
                              >
                                <ZoomOut size={16} />
                              </button>
                              <span className="text-xs text-secondary w-10 text-center">
                                {Math.round(zoom * 100)}%
                              </span>
                              <button
                                type="button"
                                title="Zoom in"
                                onClick={() =>
                                  setZoom((z) => Math.min(3, +(z + 0.1).toFixed(2)))
                                }
                                className="p-2 rounded-md border border-D0D5DD hover:bg-F2F4F5"
                              >
                                <ZoomIn size={16} />
                              </button>
                              <button
                                type="button"
                                title="Reset view"
                                onClick={resetZoom}
                                className="p-2 rounded-md border border-D0D5DD hover:bg-F2F4F5"
                              >
                                <Move size={16} />
                              </button>
                            </div>
                          </div>
                        </>
                      )}

                      <div className="px-6 flex gap-4 py-4 shrink-0">
                        <DraftButton onclkFn={onClose} text="Cancel" />
                        <AddButton
                          isDisabled={items.length === 0 || printing}
                          loading={printing}
                          onclkFn={handleSubmit}
                          type="submit"
                          className="bg-primary text-white px-8 py-2 rounded flex justify-center fw_500 text-sm"
                          text="Print"
                          prefixIcon={false}
                        />
                      </div>
                    </>
                  );
                }}
              </Formik>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PrintBarcodeModal;
